import React from 'react';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const SuccessModal = ({
  isOpen,
  onClose,
  onAction,
  user,
  isEditMode
}) => {
  if (!isOpen || !user) return null;

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose?.();
    }
  };

  const handleAction = (action) => {
    onAction?.(action);
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-orange-100 text-orange-800 border-orange-200',
      viewer: 'bg-green-100 text-green-800 border-green-200'
    };

    const roleLabels = {
      admin: 'Administrator',
      manager: 'Manager', 
      viewer: 'Viewer'
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        roleStyles?.[role] || 'bg-gray-100 text-gray-800 border-gray-200'
      )}>
        {roleLabels?.[role] || role}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {isEditMode ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isEditMode ? 'As alterações foram salvas' : 'A conta está pronta para uso'}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User Details */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 h-10 w-10">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-green-800 mb-1">
                  {user?.name}
                </h4>
                <p className="text-sm text-green-700 mb-2">
                  {user?.email}
                </p>
                <div className="flex items-center space-x-2">
                  {getRoleBadge(user?.role)}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1"></div>
                    Ativo
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="space-y-3">
            <h4 className="font-medium text-foreground">
              {isEditMode ? 'Informações atualizadas:' : 'Próximos passos:'}
            </h4>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              {isEditMode ? (
                <ul className="list-disc list-inside space-y-1">
                  <li>Perfil do usuário atualizado com novas informações</li>
                  <li>Permissões de perfil agora em vigor</li>
                  <li>Usuário verá as alterações no próximo login</li>
                </ul>
              ) : (
                <ul className="list-disc list-inside space-y-1">
                  <li>Conta do usuário foi ativada</li>
                  {user?.temporaryPassword && (
                    <li>Credenciais de acesso foram geradas</li>
                  )}
                  <li>Usuário já pode acessar o sistema com as permissões atribuídas</li>
                  <li>Considere notificar o usuário sobre a nova conta</li>
                </ul>
              )}
            </div>

            {/* Password Info for New Users */}
            {!isEditMode && user?.temporaryPassword && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <div className="flex items-start space-x-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Temporary Password Generated</p>
                    <p className="text-xs">
                      Make sure to securely share the login credentials with the user.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          {!isEditMode && (
            <Button
              variant="outline"
              onClick={() => handleAction('create_another')}
              iconName="UserPlus"
              iconPosition="left"
            >
              Create Another User
            </Button>
          )}
          <Button
            onClick={() => handleAction('back_to_list')}
            iconName="Users"
            iconPosition="left"
          >
            Back to User Management
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;